import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { adminApi } from '@/lib/api';
import ImageUpload from '@/components/admin/ImageUpload';

const AdminTeam = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await adminApi.getTeamMembers();
      setMembers(response.data);
    } catch (error) {
      toast.error('Failed to fetch team members');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditItem({
      name: '',
      role: '',
      bio: '',
      image_url: '',
      email: '',
      linkedin_url: '',
      twitter_url: '',
      order: members.length,
      is_active: true
    });
    setEditModal(true);
  };

  const openEdit = (member) => {
    setEditItem({ ...member });
    setEditModal(true);
  };

  const handleSave = async () => {
    if (!editItem.name || !editItem.role) {
      toast.error('Name and Role are required');
      return;
    }

    setSaving(true);
    try {
      if (editItem.id) {
        await adminApi.updateTeamMember(editItem.id, editItem);
        setMembers(members.map(m => m.id === editItem.id ? editItem : m));
        toast.success('Team member updated');
      } else {
        const response = await adminApi.createTeamMember(editItem);
        setMembers([...members, response.data]);
        toast.success('Team member created');
      }
      setEditModal(false);
    } catch (error) {
      toast.error('Failed to save team member');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this team member?')) return;
    try {
      await adminApi.deleteTeamMember(id);
      setMembers(members.filter(m => m.id !== id));
      toast.success('Team member deleted');
    } catch (error) {
      toast.error('Failed to delete team member');
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-team">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Team Members</h1>
          <p className="text-muted-foreground">Manage your team members</p>
        </div>
        <Button onClick={openCreate} data-testid="add-team-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Team Member
        </Button>
      </div>

      {members.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No team members yet. Add your first team member!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {members.map((member) => (
            <Card key={member.id} className={!member.is_active ? 'opacity-50' : ''}>
              <CardHeader>
                <div className="flex items-center gap-4">
                  <Avatar className="w-16 h-16">
                    <AvatarImage src={member.image_url} alt={member.name} />
                    <AvatarFallback className="bg-primary/10 text-primary text-xl">
                      {getInitials(member.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <CardTitle className="text-lg">{member.name}</CardTitle>
                    <CardDescription>{member.role}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground line-clamp-3 mb-4">{member.bio}</p>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(member)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(member.id)}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={editModal} onOpenChange={setEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editItem?.id ? 'Edit Team Member' : 'Add Team Member'}</DialogTitle>
            <DialogDescription>Fill in the team member details</DialogDescription>
          </DialogHeader>
          
          {editItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={editItem.name}
                    onChange={(e) => setEditItem({...editItem, name: e.target.value})}
                    placeholder="Full name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Role *</Label>
                  <Input
                    value={editItem.role}
                    onChange={(e) => setEditItem({...editItem, role: e.target.value})}
                    placeholder="Job title"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Bio</Label>
                <Textarea
                  value={editItem.bio}
                  onChange={(e) => setEditItem({...editItem, bio: e.target.value})}
                  rows={3}
                  placeholder="Short biography"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Profile Image</Label>
                <ImageUpload
                  value={editItem.image_url}
                  onChange={(url) => setEditItem({...editItem, image_url: url})}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editItem.email}
                  onChange={(e) => setEditItem({...editItem, email: e.target.value})}
                  placeholder="email@example.com"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>LinkedIn URL</Label>
                  <Input
                    value={editItem.linkedin_url}
                    onChange={(e) => setEditItem({...editItem, linkedin_url: e.target.value})}
                    placeholder="https://linkedin.com/in/..."
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Twitter URL</Label>
                  <Input
                    value={editItem.twitter_url}
                    onChange={(e) => setEditItem({...editItem, twitter_url: e.target.value})}
                    placeholder="https://twitter.com/..."
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Order</Label>
                  <Input
                    type="number"
                    value={editItem.order}
                    onChange={(e) => setEditItem({...editItem, order: parseInt(e.target.value) || 0})}
                  />
                </div>
                
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    checked={editItem.is_active}
                    onCheckedChange={(c) => setEditItem({...editItem, is_active: c})}
                  />
                  <Label>Active</Label>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTeam;
